/**
 * GobdExportPanel — M9c GoBD-/Z3-Prüfer-Export (Client-seitig, inert hinter Flag).
 *
 * Exportiert owner-scoped Buchhaltungsdaten (Rechnungen, Zahlungen, Ausgaben,
 * Mahnungen) als strukturierte UTF-8-CSVs für einen wählbaren Zeitraum.
 * Z3-tauglich = maschinell auswertbar mit klaren Spalten-Headern.
 *
 * Audit-Trail-Export: NICHT client-seitig verfügbar (`audit_logs read: if false`).
 * Ein prominenter Platzhalter-Hinweis zeigt dies an. Folgearbeit: Backend-Endpoint.
 *
 * Fail-closed: rendert null, solange REACT_APP_GOBD_ENABLED !== 'true'.
 * Keine Rechts-/Disclaimer-Prosa in dieser Komponente (legal-gate vermeiden).
 *
 * Daten-Ladestrategien:
 *   - Invoices: direkte Firestore-Query (Muster DatevExportPage, owner-scoped)
 *   - Payments: getPaymentsForUser() aus paymentsApi
 *   - Expenses: getExpensesForUser() aus expensesApi
 *   - Dunning events: direkte Firestore-Query (owner-scoped, no existing user-fn)
 *
 * @module GobdExportPanel
 */

import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  IconDownload,
  IconError,
  IconInfo,
  IconCsv,
  IconLock,
} from '../../icons';
import { collection, query, where, getDocs, Timestamp } from 'firebase/firestore';

import { useAuth } from '../../context/AuthContext';
import { db } from '../../firebaseConfig';
import { loadInvoicesForUser as loadInvoiceDocsForUser } from '../../utils/datev/datevSource';
import { getPaymentsForUser } from '../../api/paymentsApi';
import { getExpensesForUser } from '../../api/expensesApi';
import { downloadBlob } from '../../api/invoiceApi';
import logger from '../../utils/logger';
import { buildGobdExport, buildGobdFilename, buildGobdBlob } from '../../utils/gobdExport';
import type {
  GobdExportData,
  GobdPeriod,
  GobdExportResult,
  GobdInvoiceInput,
  GobdPaymentInput,
  GobdExpenseInput,
  GobdDunningInput,
} from '../../utils/gobdExport';
import type { Payment } from '../../types/paymentTypes';
import type { Expense } from '../../types/expenseTypes';
import type { DunningEvent } from '../../types/dunningTypes';

// ─── Feature-Flag (fail-closed) ──────────────────────────────────────────────────

const GOBD_ENABLED = process.env.REACT_APP_GOBD_ENABLED === 'true';

// ─── Datum-Helfer ─────────────────────────────────────────────────────────────────

function pad2(n: number): string {
  return String(n).padStart(2, '0');
}

function currentYearStart(): string {
  return `${new Date().getFullYear()}-01-01`;
}

function currentYearEnd(): string {
  return `${new Date().getFullYear()}-12-31`;
}

/** Extrahiert ein ISO-Datum aus Timestamp oder String. */
function toDateISO(value: string | Timestamp | null | undefined): string {
  if (!value) return '';
  if (typeof value === 'string') return value.slice(0, 10);
  if (value instanceof Timestamp) {
    const d = value.toDate();
    return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
  }
  return '';
}

// ─── Firestore invoice doc shape (relevante Felder) ──────────────────────────────

interface InvoiceDoc {
  id: string;
  userId: string;
  invoiceNumber?: string;
  invoiceDate?: string | Timestamp;
  buyerName?: string;
  totalAmount?: number;
  currency?: string;
  isCancellation?: boolean;
}

// ─── Daten-Loader ─────────────────────────────────────────────────────────────────

/**
 * Lädt owner-scoped invoices und mappt sie auf `GobdInvoiceInput` (WL-K7: die
 * Firestore-Query kommt aus dem shared `loadInvoicesForUser`; die GoBD-spezifische
 * Feld-Abbildung bleibt lokal). Verhaltens-identisch zur früheren Inline-Query.
 */
async function loadInvoicesForUser(uid: string): Promise<GobdInvoiceInput[]> {
  const rawDocs = await loadInvoiceDocsForUser<InvoiceDoc>(uid);
  return rawDocs.map((doc) => {
    // Belegdatum: top-level invoiceDate bevorzugt
    const rawDate =
      typeof doc.invoiceDate === 'string'
        ? doc.invoiceDate
        : toDateISO(doc.invoiceDate as Timestamp | undefined);
    return {
      id: doc.id,
      invoiceNumber:
        doc.invoiceNumber && doc.invoiceNumber.trim().length > 0
          ? doc.invoiceNumber
          : null,
      dateISO: rawDate || new Date().toISOString().slice(0, 10),
      buyerName: doc.buyerName ?? '',
      // totalAmount in Firestore ist in Euro (Float) → Cents (Integer)
      totalAmountCents: Math.round((doc.totalAmount ?? 0) * 100),
      currency: (doc.currency ?? 'EUR').toUpperCase(),
      isCancellation: doc.isCancellation === true,
    } satisfies GobdInvoiceInput;
  });
}

/** Mappt Payment-Dokument auf GobdPaymentInput. */
function mapPayment(p: Payment): GobdPaymentInput {
  return {
    id: p.id ?? '',
    invoiceId: p.invoiceId,
    amountCents: p.amountCents,
    paidAtISO: toDateISO(p.paidAt) || new Date().toISOString().slice(0, 10),
    method: p.method,
    source: p.source,
    reversalOfPaymentId: p.reversalOfPaymentId,
  };
}

/** Mappt Expense-Dokument auf GobdExpenseInput. */
function mapExpense(e: Expense): GobdExpenseInput {
  return {
    id: e.id ?? '',
    invoiceNumber:
      e.invoiceNumber && e.invoiceNumber.trim().length > 0
        ? e.invoiceNumber
        : null,
    dateISO: (e.expenseDate ?? '').slice(0, 10),
    supplierName: e.supplierName ?? '',
    grossCents: e.grossCents,
    netCents: e.netCents,
    vatOwedCents: e.vatOwedCents,
    status: e.status,
    taxScheme: e.taxScheme ?? '',
  };
}

/** Mappt DunningEvent-Dokument auf GobdDunningInput. */
function mapDunningEvent(ev: DunningEvent): GobdDunningInput {
  return {
    id: ev.id ?? '',
    invoiceId: ev.invoiceId,
    level: ev.level,
    sentAtISO: toDateISO(ev.sentAt) || new Date().toISOString().slice(0, 10),
    channel: ev.channel,
    feeCents: ev.feeCents ?? 0,
    interestCents: ev.interestCents ?? 0,
  };
}

/** Lädt alle owner-scoped dunning_events für einen User. */
async function loadDunningEventsForUser(uid: string): Promise<GobdDunningInput[]> {
  const snap = await getDocs(
    query(collection(db, 'dunning_events'), where('userId', '==', uid)),
  );
  return snap.docs.map((d) => {
    const ev = { id: d.id, ...(d.data() as Omit<DunningEvent, 'id'>) };
    return mapDunningEvent(ev);
  });
}

// ─── Komponente ──────────────────────────────────────────────────────────────────

export interface GobdExportPanelProps {
  /** Optionale zusätzliche CSS-Klassen für die Wrapper-Box. */
  className?: string;
}

/**
 * Eigenständiges Export-Panel für den GoBD-/Z3-Datenexport (M9c).
 *
 * Kann als alleinstehende Seite eingebunden werden oder später in die
 * `GobdPage` (M9b, legal-gated) importiert werden — lose Kopplung durch
 * eigenständige Datenladung und i18n-Namespace `gobd`.
 *
 * Fail-closed: gibt `null` zurück, wenn REACT_APP_GOBD_ENABLED !== 'true'.
 */
const GobdExportPanel: React.FC<GobdExportPanelProps> = ({ className }) => {
  const { t } = useTranslation('gobd');
  const { currentUser, effectiveScopeUid } = useAuth();

  const [fromISO, setFromISO] = useState<string>(currentYearStart);
  const [toISO, setToISO] = useState<string>(currentYearEnd);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<GobdExportResult | null>(null);

  const period: GobdPeriod = { fromISO, toISO };

  const handleLoad = useCallback(async () => {
    if (!currentUser) return;

    setLoading(true);
    setError(null);
    setResult(null);

    try {
      // Alle Entitäten parallel laden — fail-closed: kein Teil-Export aus unvollständigen Daten.
      const [rawInvoices, rawPayments, rawExpenses, rawDunning] = await Promise.all([
        loadInvoicesForUser(effectiveScopeUid),
        getPaymentsForUser(effectiveScopeUid).then((ps) => ps.map(mapPayment)),
        getExpensesForUser(effectiveScopeUid).then((es) => es.map(mapExpense)),
        loadDunningEventsForUser(effectiveScopeUid),
      ]);

      const data: GobdExportData = {
        invoices: rawInvoices,
        payments: rawPayments,
        expenses: rawExpenses,
        dunningEvents: rawDunning,
      };

      setResult(buildGobdExport(data, period));
    } catch (err) {
      logger.error('GobdExportPanel: data load failed', err);
      setError(String(err instanceof Error ? err.message : err));
    } finally {
      setLoading(false);
    }
  }, [currentUser, effectiveScopeUid, fromISO, toISO]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDownload = (csv: string, entity: string) => {
    try {
      const blob = buildGobdBlob(csv);
      downloadBlob(blob, buildGobdFilename(entity, period));
    } catch (err) {
      logger.error('GobdExportPanel: download failed', err);
    }
  };

  // Fail-closed: ohne Flag nichts rendern (nach allen Hooks — rules-of-hooks).
  if (!GOBD_ENABLED) return null;

  return (
    <div className={`rounded-xl border border-hairline bg-[rgb(var(--surface-raised-rgb)/0.5)] p-6 ${className ?? ''}`}>
      {/* Titel */}
      <div className="flex items-center gap-3 mb-5">
        <IconCsv size={20} className="text-accent-ink flex-shrink-0" />
        <div>
          <h2 className="text-lg font-semibold text-primary">{t('export.title')}</h2>
          <p className="text-muted text-xs mt-0.5">{t('export.subtitle')}</p>
        </div>
      </div>

      {/* Zeitraum-Auswahl */}
      <div className="flex flex-wrap gap-4 mb-5">
        <div className="flex flex-col gap-1">
          <label className="text-xs text-muted font-medium" htmlFor="gobd-from">
            {t('export.period.from')}
          </label>
          <input
            id="gobd-from"
            type="date"
            value={fromISO}
            onChange={(e) => {
              setFromISO(e.target.value);
              setResult(null);
            }}
            className="bg-surface border border-strong text-primary text-sm rounded-lg px-3 py-1.5 focus:outline-none focus:ring-1 focus:ring-indigo-500"
          />
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-xs text-muted font-medium" htmlFor="gobd-to">
            {t('export.period.to')}
          </label>
          <input
            id="gobd-to"
            type="date"
            value={toISO}
            onChange={(e) => {
              setToISO(e.target.value);
              setResult(null);
            }}
            className="bg-surface border border-strong text-primary text-sm rounded-lg px-3 py-1.5 focus:outline-none focus:ring-1 focus:ring-indigo-500"
          />
        </div>
        <div className="flex items-end">
          <button
            type="button"
            onClick={handleLoad}
            disabled={loading || !fromISO || !toISO}
            className="px-4 py-1.5 rounded-lg text-sm font-semibold bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed text-white transition-colors"
          >
            {loading ? t('export.loading') : t('export.loadButton')}
          </button>
        </div>
      </div>

      {/* Fehleranzeige */}
      {error && (
        <div className="bg-[var(--tone-danger-bg)] border border-[var(--tone-danger-border)] rounded-lg p-4 mb-5 flex items-start gap-3">
          <IconError size={14} className="text-[var(--tone-danger-fg)] mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-[var(--tone-danger-fg)] text-sm font-medium">{t('export.error.title')}</p>
            <p className="text-[var(--tone-danger-fg)] text-xs mt-1 font-mono">{error}</p>
          </div>
        </div>
      )}

      {/* Export-Ergebnis */}
      {result && (
        <div className="space-y-3">
          {/* Download-Buttons */}
          {(
            [
              {
                key: 'invoices',
                csv: result.invoicesCsv,
                entity: 'Rechnungen',
                count: result.counts.invoices,
                labelKey: 'export.download.invoices',
                countKey: 'export.counts.invoices',
              },
              {
                key: 'payments',
                csv: result.paymentsCsv,
                entity: 'Zahlungen',
                count: result.counts.payments,
                labelKey: 'export.download.payments',
                countKey: 'export.counts.payments',
              },
              {
                key: 'expenses',
                csv: result.expensesCsv,
                entity: 'Ausgaben',
                count: result.counts.expenses,
                labelKey: 'export.download.expenses',
                countKey: 'export.counts.expenses',
              },
              {
                key: 'dunningEvents',
                csv: result.dunningEventsCsv,
                entity: 'Mahnungen',
                count: result.counts.dunningEvents,
                labelKey: 'export.download.dunningEvents',
                countKey: 'export.counts.dunningEvents',
              },
            ] as const
          ).map((item) => (
            <div
              key={item.key}
              className="flex items-center justify-between bg-[rgb(var(--surface-base-rgb)/0.6)] border border-hairline rounded-lg px-4 py-3"
            >
              <span className="text-sm text-secondary">
                {t(item.countKey, { count: item.count })}
              </span>
              <button
                type="button"
                onClick={() => handleDownload(item.csv, item.entity)}
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md text-xs font-semibold bg-indigo-700 hover:bg-indigo-600 text-white transition-colors"
              >
                <IconDownload />
                {t(item.labelKey)}
              </button>
            </div>
          ))}

          {/* Audit-Trail-Platzhalter */}
          <div className="bg-[rgb(var(--surface-base-rgb)/0.4)] border border-strong rounded-lg px-4 py-4 mt-4">
            <div className="flex items-start gap-3">
              <IconLock size={14} className="text-faint mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-secondary text-sm font-medium">
                  {t('export.auditTrail.title')}
                </p>
                <p className="text-faint text-xs mt-1">{t('export.auditTrail.note')}</p>
              </div>
            </div>
          </div>

          {/* Verantwortungs-Hinweis (Z3 ist Grundlage, nicht Garantie — §10.1) */}
          <p className="text-faint text-xs mt-3">{t('export.responsibilityNote')}</p>

          {/* Erzeugungszeitstempel */}
          <p className="text-gray-600 text-xs text-right mt-2">
            {t('export.generatedAt', { timestamp: new Date(result.generatedAtISO).toLocaleString() })}
          </p>
        </div>
      )}

      {/* Leer-Zustand: noch keine Daten geladen */}
      {!result && !loading && !error && (
        <div className="flex items-center gap-2 text-faint text-sm">
          <IconInfo />
          <span>{t('export.emptyState')}</span>
        </div>
      )}
    </div>
  );
};

export default GobdExportPanel;
